import type { MaybeRef } from "vue"
import type { RouteLocationRaw } from "vue-router"
import type { Album, EventVault, User } from "@/models"
import type { INavigationItem } from "@/types"
import { isSameDay } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { deburr, kebabCase } from "lodash-es"
import { toValue } from "vue"
import { z } from "zod"
import { i18n } from "@/i18n"
import { EventVaultStatus, EventVaultType } from "@/models/eventVault"
import { UserRole } from "@/types"

/**
 * Used for table/card views that can be toggled between via a button group
 */
export enum ViewMode {
  TABLE = "TABLE",
  CARD = "CARD",
}

export function toSlug(value: string) {
  const normalized = deburr(value).replace(/\p{Extended_Pictographic}/gu, "")
  return kebabCase(normalized)
}

/**
 * Returns URL for guest experience navigation
 * @param page - The guest page to navigate to.
 * @param eventVaultId - The ID of the event vault.
 * @param albumId - (Optional) The ID of the album.
 * @param additionalParams - (Optional) Any additional query parameters.
 */
export function getGuestNavigationHref(page: string, eventVaultId: number, albumId?: number, additionalParams: Record<string, string> = {}) {
  const params = new URLSearchParams(additionalParams)

  if (albumId) {
    params.append("album", albumId.toString())
  }

  return `/guest/${eventVaultId}/${page}?${params.toString()}`
}

/**
 * Determines whether uploads are allowed based on the event's status, settings, and host privileges.
 * @param isHost - if the user is the host of the event
 * @param eventVault - The event vault
 * @param album Album - Optional album to determine if album uploads are available or not
 */
export function canUploadToGallery(isHost: boolean, eventVault?: EventVault, album?: Album): boolean {
  if (album?.settings.disableUploads) {
    return false
  }

  const statusAllowsUpload = [EventVaultStatus.Live, EventVaultStatus.Expiring].includes(eventVault?.status as EventVaultStatus)
  const guestUploadEnabled = !eventVault?.eventVaultSettings?.disableGuestUpload || !!album
  const isUploadWindowOpen = eventVault?.closeDate ? new Date() < new Date(eventVault.closeDate) : true

  return statusAllowsUpload && ((guestUploadEnabled && isUploadWindowOpen) || isHost)
}

/**
 * Determines whether creating albums are allowed based on the event's status and settings
 * @param eventVault - The event vault needing to add albums to
 */
export function allowCreateAlbums(eventVault?: EventVault): boolean {
  const eventStatus = eventVault?.status
  if (!eventStatus)
    return false

  const statusAllowsCreate = [
    EventVaultStatus.Live,
    EventVaultStatus.Expiring,
    EventVaultStatus.Draft,
    EventVaultStatus.Ready,
  ].includes(eventStatus)

  const albumsEnabled = eventVault?.eventVaultSettings?.albumsEnabled

  return Boolean(statusAllowsCreate && albumsEnabled)
}

/**
 * Determines whether the freemium advertising banner should be shown.
 * Suppressed for HonorPix-branded environments and Memorial event types.
 * @param eventVault - The event vault to evaluate.
 */
export function canShowFreemiumBanner(eventVault?: EventVault): boolean {
  if (!eventVault?.freemiumMode) {
    return false
  }

  const isHonorpix = (import.meta.env.VITE_BRAND || "").trim().toLowerCase() === "honorpix"
  const isMemorial = eventVault.eventType === EventVaultType.Memorial

  return !isHonorpix && !isMemorial
}

/**
 * Generates navigation items for the host event based on event settings.
 * Adds an additional "Manage Templates" item if advanced templates are enabled in the event settings.
 * @param eventVault - The event vault containing event settings and details.
 * @param user
 */
export function getEventNavigation(eventVault?: EventVault, user?: User): INavigationItem[] {
  if (!eventVault)
    return []

  const { t } = i18n.global

  const eventVaultId = eventVault.id

  const navigationItems: INavigationItem[] = [
    {
      label: t("host_menu_items.event_details"),
      href: eventVaultId ? `/event/edit/${eventVaultId}` : "",
      icon: "CalendarLines",
    },
    {
      label: t("host_menu_items.event_settings"),
      href: eventVaultId ? `/event/advancedsettings/${eventVaultId}` : "",
      icon: "SettingsCog",
    },
    {
      label: t("host_menu_items.manage_media"),
      href: eventVaultId ? `/event/managemedia/${eventVaultId}` : "",
      icon: "ImageEdit",
    },
    {
      label: t("host_menu_items.manage_albums"),
      href: eventVaultId ? `/event/albums/${eventVaultId}` : "",
      icon: "AlbumCog",
    },
  ]

  if (user?.roles.includes(UserRole.Affiliate)) {
    navigationItems.push({
      label: t("host_menu_items.affiliates"),
      href: `https://guestpix.com/affiliate-area-login/`,
      icon: "Dollar",
    })
  }

  if (eventVault.eventVaultSettings?.allowEventTemplate) {
    navigationItems.push({
      label: t("host_menu_items.manage_templates"),
      href: `/template/apply?eventvaultid=${eventVaultId}`,
      icon: "ImageEdit",
    })
  }

  return navigationItems
}

/**
 * Converts a date to a specific timezone.
 *
 * @param date - The date to convert
 * @param timezone - The target timezone (e.g., 'America/New_York', 'Europe/London')
 * @returns The date converted to the specified timezone, or the original date if timezone is not provided or conversion fails
 */
export function convertDateTimezone(date: Date, timezone: string) {
  if (timezone) {
    const zonedDate = toZonedTime(date, timezone)
    if (zonedDate instanceof Date && !Number.isNaN(zonedDate.getTime()))
      return zonedDate
  }

  return date
}

type DateFormatter = (value: number | Date, key?: string | Intl.DateTimeFormatOptions) => string

/**
 * Formats event start and optional end dates for display.
 * Timed same-day events show the full start datetime and end time only.
 * All-day events without a timezone show the start date only.
 * Multi-day events show the full range.
 */
export function formatEventVaultDateRange(
  eventDate: Date,
  eventEndDate: Date | undefined,
  timezone: string | undefined,
  d: DateFormatter,
): string {
  const isTimedEvent = Boolean(timezone)
  const dateFormat = isTimedEvent ? "long" : "short"

  const start = timezone ? convertDateTimezone(eventDate, timezone) : eventDate
  const end = eventEndDate
    ? timezone
      ? convertDateTimezone(eventEndDate, timezone)
      : eventEndDate
    : undefined

  if (!end || (!isTimedEvent && isSameDay(start, end))) {
    return d(start, dateFormat)
  }

  if (isTimedEvent && isSameDay(start, end)) {
    return `${d(start, dateFormat)} – ${d(end, { hour: "2-digit", minute: "2-digit" })}`
  }

  return `${d(start, dateFormat)} – ${d(end, dateFormat)}`
}

/**
 * Reads a cookie value by name from document.cookie.
 * Returns null if the cookie is not present.
 */
export function getCookie(name: string): string | null {
  const match = document.cookie
    .split(/;\s*/)
    .find(row => row.startsWith(`${name}=`))

  if (!match)
    return null

  const raw = match.slice(name.length + 1)

  try {
    return decodeURIComponent(raw)
  }
  catch {
    return raw
  }
}

export function passwordValidation(opts: { optional: MaybeRef<boolean> } = { optional: false }) {
  const optional = toValue(opts.optional)
  const { t } = i18n.global

  if (optional) {
    return z.string()
      // ensure at least 1 uppercase character
      .refine(password => password.length < 1 || /[A-Z]/.test(password), {
        message: t("password_validation.password_uppercase_char"),
      })
      // ensure at least 1 lowercase character
      .refine(password => password.length < 1 || /[a-z]/.test(password), {
        message: t("password_validation.password_lowercase_char"),
      })
      // ensure at least 1 numeric character
      .refine(password => password.length < 1 || /\d/.test(password), {
        message: t("password_validation.password_numeric_char"),
      })
  }

  return z.string()
    .min(8, t("password_validation.invalid_password_length", { length: 8 }))
    // ensure at least 1 uppercase character
    .refine(password => /[A-Z]/.test(password), {
      message: t("password_validation.password_uppercase_char"),
    })
    // ensure at least 1 lowercase character
    .refine(password => /[a-z]/.test(password), {
      message: t("password_validation.password_lowercase_char"),
    })
    // ensure at least 1 numeric character
    .refine(password => /\d/.test(password), {
      message: t("password_validation.password_numeric_char"),
    })
}

export function locationValidation() {
  const { t } = i18n.global

  return z
    .object({
      locationId: z.number().optional(),
      googleId: z.string().optional(),
      otherId: z.string().optional(),
      name: z.string().optional(),
      latitude: z.number().optional(),
      longitude: z.number().optional(),
      streetNumber: z.string().optional(),
      streetAddress: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      region: z.string().optional(),
      zipCode: z.string().optional(),
      country: z.string().optional(),
      isPrivate: z.boolean().optional(),
      formattedAddress: z.string().optional(),
      website: z.string().optional(),
      establishment: z.boolean().optional(),
      pointOfInterest: z.boolean().optional(),
      displayAddress: z.string().optional(),
    })
    .optional()
    // ensures a formatted address and a Google ID is provided
    // so that custom addresses without Google verification are not allowed
    .refine(
      loc => !loc?.displayAddress || loc.googleId,
      t("invalid_location_validation"),
    )
}

/**
 * Returns the CTA for the create event page
 */
export function getCreateEventCTA(user?: User | null): { label: string, href?: string, to?: RouteLocationRaw } {
  const { t } = i18n.global

  const PRICING_URL = "https://guestpix.com/pricing/"
  const SUBSCRIPTIONS_URL = "https://guestpix.com/my-account/subscriptions/"

  const isAffiliate = user?.roles.includes(UserRole.Affiliate)
  const hasSubscription = Boolean(user?.packageSubscription?.packageSubscriptionId)

  if (hasSubscription) {
    const subscription = user?.packageSubscription
    const limitReached = (subscription?.eventsUsed ?? 0) >= (subscription?.maxEvents ?? 0)

    if (limitReached) {
      return { label: t("upgrade_subscription"), href: SUBSCRIPTIONS_URL }
    }

    return {
      label: t("create_event"),
      to: { path: "/onboard/setup", query: { email: user?.email, token: subscription?.token, packageSubscriptionId: subscription?.packageSubscriptionId } },
    }
  }

  if (isAffiliate) {
    return { label: t("add_package"), href: `${PRICING_URL}?ref=${user?.affiliateId}` }
  }

  return {
    label: t("add_package"),
    href: PRICING_URL,
  }
}

/**
 * Checks if a URL is an Amazon URL
 * @param url - The URL to check if it is an Amazon URL
 * @returns True if the URL is an Amazon URL, false otherwise
 */
export function isAmazonUrl(url: string) {
  const trimmed = url.trim()
  if (!trimmed)
    return false

  try {
    const hostname = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`).hostname.toLowerCase()
    return hostname === "amzn.to"
      || hostname === "a.co"
      || hostname === "amazon.com"
      || hostname.endsWith(".amazon.com")
      || /^amazon\.[a-z.]+$/.test(hostname)
      || /\.amazon\.[a-z.]+$/.test(hostname)
  }
  catch {
    return false
  }
}

/**
 * Safely tears down a `<video>` element's media pipeline before it is removed
 * from the DOM. On iOS Safari, removing a `<video>` while it still has an
 * active `src`/buffering operation can throw an `InvalidStateError`. Pausing,
 * clearing any `<source>` children, and calling `load()` cancels any pending
 * media operations first, avoiding the error.
 * @param video - The video element to unload, if any.
 */
export function safelyUnloadVideoElement(video?: HTMLVideoElement | null) {
  if (!video)
    return

  try {
    video.pause()
    video.innerHTML = ""
    video.load()
  }
  catch {
    // iOS Safari can throw a synchronous InvalidStateError when pause() or
    // load() is called while a play() promise is still pending. Swallow the
    // error here - the element will be removed from the DOM regardless.
  }
}

/**
 * Attempts to play a `<video>` element, silently swallowing any rejection.
 * Browsers can reject `play()` for a number of expected, non-actionable
 * reasons (autoplay blocked, the play request was interrupted by a pause/
 * load, or the element wasn't in a state to accept it yet), all of which
 * would otherwise surface as unhandled promise rejections. Synchronous throws
 * from `play()` are treated the same way as rejections.
 * @param video - The video element to play, if any.
 * @param onRejected - Optional callback invoked if the play request is rejected.
 */
export function safelyPlayVideo(video?: HTMLVideoElement | null, onRejected?: () => void) {
  if (!video)
    return

  try {
    video.play()?.catch(() => onRejected?.())
  }
  catch {
    onRejected?.()
  }
}
